<template>
  <div class="flex flex-col h-full">
    

    <!-- Header for empty state -->
    <div  v-if="(!weedResults || Object.keys(weedResults).length == 0) && (!biosecurityResults || Object.keys(biosecurityResults).length == 0)" class="px-6 py-4 border-b bg-background">
      <div class="flex justify-between items-start">
        <div>
          <h3 class="text-lg font-semibold mb-2 group hover:cursor-pointer relative"
              @click="() => refreshWithForceFlag(true)">
            <Icon
              v-if="weedsLoadingState === 'pending' || pestsLoadingState === 'pending'"
              name="line-md:loading-loop"
              class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-6 group-hover:translate-x-0 transition-all duration-200 text-muted-foreground mr-2 top-1"
            />
            <Icon
              v-else
              name="lsicon:lightning-filled"
              class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-6 group-hover:translate-x-0 transition-all duration-200 text-muted-foreground mr-2 top-1"
            />
            <div class="relative group-hover:translate-x-6 transition-all duration-200">
              Biosecurity Assessment
            </div>
          </h3>
          <p class="text-sm text-muted-foreground max-w-2xl">
            Identifies potential biosecurity threats by analyzing pest associations and host relationships.
          </p>
        </div>
      </div>
    </div>

    <!-- Loading state - only show when both sections have no data and at least one is loading -->
    <div v-if="(weedsLoadingState === 'pending' || pestsLoadingState === 'pending') &&
               (!weedResults || Object.keys(weedResults).length === 0) &&
               (!biosecurityResults || Object.keys(biosecurityResults).length === 0)"
         class="flex items-center justify-center py-4">
      <Loader2 class="w-6 h-6 animate-spin" />
      <span class="ml-2">Loading biosecurity validations...</span>
    </div>

    <!-- No results state -->
    <div v-else-if="biosecurityValidationsAttempted && displayResults.length === 0 &&
                    (!weedResults || Object.keys(weedResults).length === 0) &&
                    weedsLoadingState !== 'pending' && pestsLoadingState !== 'pending'"
         class="flex flex-col items-center justify-center py-8 px-4">
      <Icon name="solar:database-bold" class="w-16 h-16 text-muted-foreground mb-4" />
      <h3 class="text-lg font-medium mb-2">No Biosecurity Data Found</h3>
      <p class="text-muted-foreground text-center max-w-md">
        No biosecurity threats were identified for the species in your project. This could mean the species are low-risk or data is not available in the EPPO Global Database.
      </p>
      <NuxtLink
        href="https://gd.eppo.int/"
        target="_blank"
        class="text-primary hover:underline text-sm flex items-center gap-1 mt-4"
      >
        <span>Visit EPPO Database</span>
        <Icon name="lucide:external-link" class="w-3 h-3" />
      </NuxtLink>
    </div>

    <!-- Main content -->
    <ScrollArea v-else class="h-[calc(100vh-18em)] w-full pr-2 scroll-thumb">
      <div class="flex flex-col gap-8 px-2 pb-8">

        <!-- Weeds Section -->
        <section
          id="weeds"
          ref="weedsSection"
          class="scroll-mt-20"
        >
          <div class="flex justify-between items-start pb-6 px-6 py-4">
            <div>
              <h3 
                class="text-md font-[600] mb-2 group hover:cursor-pointer relative"
                @click="() => refreshWeeds(true)"
              >
                <Icon
                  v-if="weedsLoadingState === 'pending'"
                  name="line-md:loading-loop"
                  class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-5 group-hover:translate-x-1 transition-all duration-200 text-muted-foreground mr-2 top-1"
                />
                <Icon
                  v-else
                  name="lsicon:lightning-filled"
                  class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-5 group-hover:translate-x-1 transition-all duration-200 text-muted-foreground mr-2 top-1"
                />
                <div class="relative transition-all duration-200 font-intervariable pl-0" :class="[weedsLoadingState === 'pending' ? 'translate-x-6' : 'group-hover:translate-x-6']">Weed Assessment</div>
              </h3>
              <p class="text-sm text-muted-foreground max-w-2xl">
                Identifies potential weed species by querying the Global Compendium of Weeds database. 
                Shows weed status, distribution data, and location relevance for project planning.
              </p>
            </div>
          </div>

          <div v-if="weedResults && Object.keys(weedResults).length > 0" class="px-6 relative">
            <!-- Loading overlay for weeds section when refreshing -->
            <div v-if="weedsLoadingState === 'pending'"
                 class="absolute inset-0 bg-background/80 backdrop-blur-sm z-20 flex items-center justify-center rounded-lg">
              <div class="flex items-center gap-2">
                <Loader2 class="w-5 h-5 animate-spin" />
                <span class="text-sm">Updating weed data...</span>
              </div>
            </div>
            
            <!-- Show message if no weeds found -->
            <div v-else-if="Object.values(weedResults).every(result => !result.isWeed)" class="py-8 px-4 text-center flex flex-col items-center justify-center">
              <Icon name="ic:baseline-grass" class="w-12 h-12 text-muted-foreground mb-4" />
              <h3 class="text-lg font-medium mb-2">No Weed Data Found</h3>
              <p class="text-muted-foreground text-center max-w-md">
                We couldn't find any weed information for the species in your schedule. This means the data is not available in the <a href="http://www.hear.org/gcw/" target="_blank" class="text-primary hover:underline">Global Compendium of Weeds</a>.
              </p>
            </div>
            
            <!-- Simplified weed cards -->
            <div class="space-y-3">
              <div v-for="(color, colorIndex) in ['orange', 'yellow', 'green']" :key="color">
                <div v-for="weed in (getWeedsByColor as any)[color]" :key="weed.speciesName">
                  <Card class="border border-gray-200 rounded-lg overflow-hidden">
                    <CardHeader class="pb-3">
                      <div class="flex items-center justify-between">
                        <div class="flex items-start gap-3">
                          <Icon name="ic:baseline-grass" class="w-7 h-7 text-gray-600" />
                          <div>
                            <CardTitle class="font-[400] font-intervariable truncate text-lg">{{ weed.speciesName }}</CardTitle>
                            <div class="flex gap-2 mt-1">
                              <Badge v-if="weed.species.locationRelevant?.length"
                                     class="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                                {{ weed.species.locationRelevant.length }} Site specific!
                              </Badge>
                              <Badge v-for="status in getWeedStatuses(weed.species.weedData)"
                                     :key="status"
                                     :class="{
                                       'hover:bg-red-200 bg-red-100 text-red-800': status.toLowerCase().includes('noxious'),
                                       'hover:bg-orange-200 bg-orange-100 text-orange-800': status.toLowerCase().includes('environmental'),
                                       'hover:bg-yellow-200 bg-yellow-100 text-yellow-800': !status.toLowerCase().includes('noxious') && !status.toLowerCase().includes('environmental')
                                     }"
                                     class="text-xs px-2 py-1 rounded">
                                {{ status }}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Button
                          v-if="weed.species.weedData?.length > 0"
                          variant="ghost"
                          size="sm"
                          @click="toggleWeedAccordion(weed.speciesName)"
                          class="p-1"
                        >
                          <Icon
                            name="lucide:chevron-down"
                            class="w-5 h-5 transition-transform duration-200"
                            :class="{ 'rotate-180': expandedWeedAccordions[weed.speciesName] }"
                          />
                        </Button>
                      </div>
                    </CardHeader>
                    
                    <!-- Expandable content -->
                    <div v-if="expandedWeedAccordions[weed.speciesName]" class="px-6 pb-4">
                      <div class="border-t pt-4">
                        <div v-if="weed.species.weedData?.[0]?.data_sources" class="space-y-2">
                          <h4 class="text-sm font-medium text-gray-700">Sources:</h4>
                          <ul class="space-y-1">
                            <li v-for="source in (weed.species.weedData[0].data_sources as any[]).filter((s: any) => typeof s === 'string' && !s.includes('<b>'))"
                                :key="source"
                                class="text-sm text-gray-600">
                              • {{ source }}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
              
             

          </div>
          <div v-else class="flex flex-col items-center justify-center py-8 px-4">
            <div class="!squircle-15 w-fit h-fit flex items-center justify-center">
            <Button
              @click="() => refreshWeeds(true)"
              size="lg"
              class="flex items-center gap-2 px-6 py-3 squircle-15"
              :disabled="weedsLoadingState === 'pending'"
            >
              <Icon
                v-if="weedsLoadingState === 'pending'"
                name="line-md:loading-loop"
                class="w-5 h-5"
              />
              <Icon
                v-else
                name="lsicon:lightning-filled"
                class="w-5 h-5"
              />
              {{ weedsLoadingState === 'pending' ? 'Checking...' : 'Check for Weeds' }}
            </Button>
            </div>
            
            <p class="text-sm text-muted-foreground text-center max-w-md mt-4">
              Click to check if any species are listed as weeds in the <a href="http://www.hear.org/gcw/" target="_blank" class="text-primary hover:underline">Global Compendium of Weeds</a>
            </p>
          </div>
        </section>

        <!-- Pests Section -->
        <section
          id="pests"
          ref="pestsSection"
          class="scroll-mt-20"
        >
          <div class="flex justify-between items-start pb-6 px-6 py-4">
            <div>
              <h3 
                class="text-md font-[600] mb-2 group hover:cursor-pointer relative"
                @click="() => refreshWithForceFlag(true)"
              >
                <Icon
                  v-if="pestsLoadingState === 'pending'"
                  name="line-md:loading-loop"
                  class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-5 group-hover:translate-x-1 transition-all duration-200 text-muted-foreground mr-2 top-1"
                />
                <Icon
                  v-else
                  name="lsicon:lightning-filled"
                  class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-5 group-hover:translate-x-1 transition-all duration-200 text-muted-foreground mr-2 top-1"
                />
                <div class="relative transition-all duration-200 font-intervariable pl-0" :class="[pestsLoadingState === 'pending' ? 'translate-x-6' : 'group-hover:translate-x-6']">Pest Assessment</div>
              </h3>
              <p class="text-sm text-muted-foreground max-w-2xl">
                Identifies potential biosecurity threats by analyzing pest associations and host relationships. 
                Each card shows pest images and their relationships to plant species, helping assess potential risks and management needs.
              </p>
            </div>
          </div>

          <div v-if="displayResults.length > 0" class="px-6 relative">
            <!-- Loading overlay for pests section when refreshing -->
            <div v-if="pestsLoadingState === 'pending'"
                 class="absolute inset-0 bg-background/80 backdrop-blur-sm z-20 flex items-center justify-center rounded-lg">
              <div class="flex items-center gap-2">
                <Loader2 class="w-5 h-5 animate-spin" />
                <span class="text-sm">Updating pest data...</span>
              </div>
            </div>
            
            <div v-for="(group, groupIndex) in displayResults" :key="`group-${groupIndex}`" class="mb-4">
              <Card v-if="group.data?.pests?.filter(pest => pest.name).length" 
              class="relative overflow-hidden squircle-20 rounded-2xl shadow-none" 
              :class="group.data.isInfoCard ? 'border-muted/30 border-[1px]' : 'border-primary/20 border-[1px]'">
          <div class="absolute inset-0 bg-background"></div>
          <CardHeader class="relative z-10 w-full">
            <!-- chevron - only show for non-info cards -->
             <Button v-if="!group.data.isInfoCard" @click="toggleAccordion(group.species[0])" variant="text" class="absolute top-4 right-4"> 
               <Icon 
                 name="lucide:chevron-down" 
                 class="w-5 h-5 text-muted-foreground transition-transform duration-200" 
                 :class="{ 'rotate-180': expandedAccordions[group.species[0]] }"
               />
              </Button>
               <CardTitle class="flex items-center items-start mb-2 text-lg w-[80%]">
              <Icon v-if="!group.data.isInfoCard" name="ph:bug-fill" class="text-primary mr-2 w-7 h-7 relative" />
              
              <span class="font-[400] font-intervariable truncate">{{ group.species.join(', ') }}</span>
              
            </CardTitle>
            <CardDescription v-if="!group.data.isInfoCard" class="flex flex-nowrap gap-1 mt-2 overflow-x-hidden scale-[0.9] origin-left h-5 max-h-5 -mr-5">
              <Badge v-if="group.data?.pests?.filter((p: Pest) => p.type === 'Major host').length" 
                     class="select-none whitespace-nowrap hover:bg-background hover:text-primary bg-red-500 border-[1.5px] border-primary text-white font-light text-[0.8em] p-0 px-1 flex-shrink-0">
                Major host {{ group.data?.pests?.filter((p: Pest) => p.type === 'Major host').length }}
              </Badge>
              <Badge v-if="group.data?.pests?.filter((p: Pest) => p.type === 'Host').length" 
                     class="select-none whitespace-nowrap hover:bg-background hover:text-primary bg-orange-400 border-[1.5px] border-primary text-white font-light text-[0.8em] p-0 px-1 flex-shrink-0">
                Host {{ group.data?.pests?.filter((p: Pest) => p.type === 'Host').length }}
              </Badge>
              <Badge v-if="group.data?.pests?.filter((p: Pest) => ['Wild/Weed', 'Weed/wild'].includes(p.type)).length" 
                     class="select-none whitespace-nowrap hover:bg-background hover:text-primary bg-lime-600 border-[1.5px] border-primary text-white font-light text-[0.8em] p-0 px-1 flex-shrink-0">
                Weed/wild {{ group.data?.pests?.filter((p: Pest) => ['Wild/Weed', 'Weed/wild'].includes(p.type)).length }}
              </Badge>
            </CardDescription>
          </CardHeader>
          <CardContent class="relative z-10 text-white">
            <!-- Special display for info cards -->
            <div v-if="group.data.isInfoCard" class="py-4 px-2 text-center">
              <div class="flex flex-col items-center justify-center gap-3">
                <Icon name="solar:database-bold" class="w-10 h-10 text-muted-foreground" />
                <p class="text-muted-foreground">No biosecurity data was found for this species in the EPPO Global Database.</p>
                <NuxtLink 
                  href="https://gd.eppo.int/" 
                  target="_blank" 
                  class="text-primary hover:underline text-sm flex items-center gap-1 mt-2"
                >
                  <span>Visit EPPO Database</span>
                  <Icon name="lucide:external-link" class="w-3 h-3" />
                </NuxtLink>
              </div>
            </div>
            <!-- Normal content for pest cards -->
            <template v-else>
              <div class="flex items-center gap-2 mb-4">
                <div v-for="(pest, index) in (group.data.displayPests || []).slice(0, 5)" 
                  :key="index"
                  class="relative w-12 h-12"
                >
                  <NuxtLink 
                    :href="`https://gd.eppo.int/taxon/${pest?.name?.match(/\(([A-Z]+)\)/)?.[1] || ''}`"
                    target="_blank"
                    class="block w-full h-full rounded-full overflow-hidden border-[1.5px] relative transition-all duration-300 ease-out"
                    :class="{
                      'border-red-500': pest.type === 'Major host',
                      'border-orange-400': pest.type === 'Host',
                      'border-lime-600': pest.type === 'Weed/wild' || pest.type === 'Wild/Weed',
                      'border-primary': !pest.type,
                      'hover-expanded': hoveringStates[pest.name]
                    }"
                  >
                    <!-- Show fallback icon if no image -->
                    <div v-if="!pest.imageUrl" class="w-full h-full flex items-center justify-center bg-muted/30 aspect-square">
                      <Icon name="ph:bug-fill" class="w-6 h-6 text-muted-foreground/70" />
                    </div>
                    
                    <Lens
                      v-else
                      :hovering="hoveringStates[pest.name] || false"
                      @hover-update="(value: boolean) => updateHoveringState(pest.name, value)"
                      :lens-size="180"
                      :zoom-factor="10"
                    >
                      <div class="relative w-full h-full">
                        <!-- Thumbnail version -->
                        <NuxtImg 
                          v-show="!hoveringStates[pest.name]"
                          :src="pest.imageUrl" 
                          class="w-full h-full object-cover transition-transform duration-200"
                          :alt="pest.name?.replace(/\s*\([^)]*\)\s*/g, '')"
                          loading="lazy"
                          width="120"
                          height="120"
                          sizes="120px"
                          quality="90"
                          @error="handleImageError(pest)"
                        />
                        <!-- High-res version -->
                        <NuxtImg 
                          v-show="hoveringStates[pest.name]"
                          :src="pest.imageUrl" 
                          class="w-full h-full object-cover"
                          :alt="pest.name?.replace(/\s*\([^)]*\)\s*/g, '')"
                          width="1000"
                          height="1000"
                          sizes="1000px"
                          quality="100"
                          :preload="true"
                          @error="handleImageError(pest)"
                        />
                      </div>
                    </Lens>
                  </NuxtLink>
                </div>
                <div v-if="getRemainingPestsCount(group.data)" 
                  class="text-primary text-md font-medium cursor-pointer hover:opacity-80"
                  @click="toggleAccordion(group.species[0])"
                >
                  +{{ (group.data.totalPests ?? 0) - (group.data.displayPests?.length || 0) }}
                </div>
              </div>
              <div v-if="group.data?.scheduleChanged" class="mb-2">
                <Badge class="bg-yellow-500 text-white">Schedule Changed</Badge>
              </div>
              <div v-if="group.data?.pests?.length > 0" class="mt-2">
                <Accordion v-if="group.data?.pests?.filter((pest: Pest) => pest.name).length > 0" 
                          type="single" 
                          collapsible 
                          class="w-full"
                          :model-value="expandedAccordions[group.species[0] || '']"
                          @update:model-value="(value) => updateAccordionState(String(group.species?.[0] ?? ''), value)"
                >
                  <AccordionItem :value="group.species[0] || ''" :data-value="group.species[0] || ''" class="accordion-item">
                    <AccordionTrigger class="hidden">{{ group.data?.pests?.length }} pest{{ group.data?.pests?.length > 1 ? 's' : '' }}</AccordionTrigger>
                    <AccordionContent :key="`content-${group.species[0]}-${expandedAccordions[group.species[0]] || 'closed'}`" class="text-sm border-b-0">
                      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        <div v-for="pest in group.data?.pests?.filter((pest: Pest) => pest.name)" 
                            :key="pest?.name" 
                            class="text-xs"
                        >
                          <NuxtLink 
                            :href="`https://gd.eppo.int/taxon/${pest?.name?.match(/\(([A-Z]+)\)/)?.[1] || ''}`" 
                            target="_blank" 
                            class="flex items-center rounded-lg py-1.5 px-2 transition-colors"
                            :class="{
                              'bg-red-500/10 hover:bg-red-500/20': pest.type === 'Major host',
                              'bg-orange-400/10 hover:bg-orange-400/20': pest.type === 'Host',
                              'bg-lime-600/10 hover:bg-lime-600/20': pest.type === 'Weed/wild' || pest.type === 'Wild/Weed'
                            }"
                          >
                            <div class="aspect-square rounded-2xl w-6 h-6 flex items-center justify-center mr-2">
                              <Icon 
                                name="ph:bug-fill" 
                                class="w-5 h-5"
                                :class="{
                                  'text-red-500/70': pest.type === 'Major host',
                                  'text-orange-400/70': pest.type === 'Host',
                                  'text-lime-600/70': pest.type === 'Weed/wild' || pest.type === 'Wild/Weed'
                                }"
                              />
                            </div>
                            <span class="inline-block text-foreground/90 truncate max-w-full">
                              {{ pest?.name?.replace(/\s*\([^)]*\)\s*/g, '') }}
                            </span>
                          </NuxtLink>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </template>
          </CardContent>
            </Card>
            </div>
          </div>
          <div v-else class="flex flex-col items-center justify-center py-8 px-4">
            <Button
              @click="() => refreshWithForceFlag(true)"
              size="lg"
              class="flex items-center gap-2 px-6 py-3 squircle-15"
              :disabled="pestsLoadingState === 'pending'"
            >
              <Icon
                v-if="pestsLoadingState === 'pending'"
                name="line-md:loading-loop"
                class="w-5 h-5"
              />
              <Icon
                v-else
                name="lsicon:lightning-filled"
                class="w-5 h-5"
              />
              {{ pestsLoadingState === 'pending' ? 'Checking...' : 'Check for Pests' }}
            </Button>
            <p class="text-sm text-muted-foreground text-center max-w-md mt-4">
              Click to check for biosecurity threats and pest associations
            </p>
          </div>
        </section>

      </div>
    </ScrollArea>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { useValidation } from '@/composables/useValidation'
import { useProjectStore } from '@/stores/projectStore'
import { useValidationResultsStore } from '@/stores/validationResultsStore'
import { useBiosecurityResultsStore } from '@/stores/biosecurityResultsStore'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loader2 } from 'lucide-vue-next'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import VTooltip from '@/components/VTooltip.vue'
import Lens from '@/components/Lens.vue'
import { toolDefinitions } from '@/config/tools'
import { useElementSize } from '@vueuse/core'
import { useIntersectionObserver } from '@vueuse/core'

import type { MitterEvents } from '@/types/mitterEvents'
import { isEqual } from 'lodash-es'

interface Host {
  name: string;
  type: 'Major host' | 'Host' | string;
}

interface Pest {
  name: string;
  type: 'Major host' | 'Host' | 'Wild/Weed' | 'Weed/wild' | string;
  imageUrl?: string | null;
  relationshipType?: 'direct' | 'indirect' | string;
  source?: 'eppo' | 'padil' | string;
}

interface ValidationData {
  isBiosecurityThreat: boolean;
  hosts: Host[];
  pests: Pest[];
  images?: string[];
  displayPests?: Pest[];
  totalPests?: number;
  scheduleChanged?: boolean;
  isInfoCard?: boolean;
}

// Define the props
const props = defineProps<{
  project_id: string;
  panel_id?: string;
}>();

const projectStore = useProjectStore()
const validationResultsStore = useValidationResultsStore()
const biosecurityResultsStore = useBiosecurityResultsStore()
const { selectedProject } = storeToRefs(projectStore)
const { updateBiosecurityResults } = biosecurityResultsStore
const { validateAndRefreshBiosecurity, validateWeeds, validationLoadingState, weedsLoadingState, pestsLoadingState, biosecurityValidationsAttempted } = useValidation()
const mitter = useMitter()

// Local ref for storing validation results
const biosecurityResults = ref<Record<string, ValidationData>>({})
const weedResults = ref<Record<string, any>>({})

useSquircleObserver()

// Weed status color mapping
  const weedStatusColors = {
    "orange": [
      "weed",
      "agricultural weed",
      "environmental weed",
      "noxious weed"
    ],
    "yellow": [
      "cultivation escape",
      "naturalised",
      "garden thug",
      "sleeper weed"
    ],
    "green": [
      "introduced_species",
      "exotic",
      "naturalised_species",
      "garden_escape",
      "cultivation_escape",
      "casual alien"
    ]
  }

  // Computed property to group weeds by color
  const getWeedsByColor = computed(() => {
    const grouped = {
      orange: [] as Array<{speciesName: string, species: any}>,
      yellow: [] as Array<{speciesName: string, species: any}>,
      green: [] as Array<{speciesName: string, species: any}>
    }

    if (!weedResults.value) return grouped

    Object.entries(weedResults.value).forEach(([speciesName, species]) => {
      if (!species.isWeed) return
      
      const statuses = species.weedData?.flatMap((w: any) => w.statuses || []) || []
      const color = getWeedStatusColor(statuses)
      
      if (color === 'orange' || color === 'yellow' || color === 'green') {
        grouped[color].push({ speciesName, species })
      }
    })

    return grouped
  })

// Weed descriptions
const weedDescriptions = {
  "quarantine_weed": {
    "title": "Quarantine Weed",
    "tagline": "Entry Prohibited.",
    "description": "A high-risk species that is illegal to import into Australia under national biosecurity laws."
  },
  "noxious_weed": {
    "title": "Noxious Weed",
    "tagline": "Action Required by Law.",
    "description": "A declared species that landholders are legally required to control, contain, or eradicate."
  },
  "environmental_weed": {
    "title": "Environmental Weed",
    "tagline": "Threat to Nature.",
    "description": "A species that invades and damages native ecosystems, disrupting natural processes and threatening native plants and animals."
  },
  "sleeper_weed": {
    "title": "Sleeper Weed",
    "tagline": "On the Watchlist.",
    "description": "An introduced species that has established but is not yet widespread or causing major harm. It shows clear potential to become a serious threat in the future."
  },
  "weed_general": {
    "title": "Weed (General)",
    "description": "A plant causing economic or management problems in areas like farms, gardens, or commercial nurseries. The specific impact can vary."
  },
  "native_weed": {
    "title": "Native Weed",
    "tagline": "A Problematic Native.",
    "description": "A species native to Australia that has become a weed, often by spreading outside its original habitat or becoming overly dominant within it."
  },
  "naturalised_species": {
    "title": "Naturalised Species",
    "tagline": "Established in the Wild.",
    "description": "An introduced species that now has a self-sustaining population, reproducing and spreading without human help. It may not yet be causing harm but has significant weed potential."
  },
  "garden_escape": {
    "title": "Garden Escape",
    "tagline": "Jumped the Fence.",
    "description": "A cultivated plant that has spread from a managed area, like a garden or park, into the surrounding environment. This often happens via seed dispersal or dumped green waste."
  },
  "cultivation_escape": {
    "title": "Cultivation Escape",
    "tagline": "Escaped from Cultivation.",
    "description": "A species that has spread from any cultivated setting, including private gardens, commercial crops, or pastures."
  },
  "introduced_species": {
    "title": "Introduced Species",
    "tagline": "Not from Here.",
    "description": "A species brought to an area by people, either intentionally (e.g., for gardens, farming) or accidentally. It may or may not be established in the wild."
  },
  "casual_alien": {
    "title": "Casual Alien",
    "tagline": "Just Visiting.",
    "description": "An introduced species that may appear and survive for a short time but fails to establish a long-term, self-sustaining population."
  },
  "exotic": {
    "title": "Exotic",
    "tagline": "Status Unknown.",
    "description": "A non-native species that is present in an area, but its ability to survive, reproduce, or spread has not yet been determined."
  }
}

// Function to get weed status color
const getWeedStatusColor = (statuses: string[]) => {
  if (!statuses || statuses.length === 0) return 'muted'
  
  const lowerStatuses = statuses.map(s => s.toLowerCase())
  
  // Check for orange (highest priority)
  if (lowerStatuses.some(status => weedStatusColors.orange.includes(status))) {
    return 'orange'
  }
  
  // Check for yellow (medium priority)
  if (lowerStatuses.some(status => weedStatusColors.yellow.includes(status))) {
    return 'yellow'
  }
  
  // Check for green (lowest priority)
  if (lowerStatuses.some(status => weedStatusColors.green.includes(status))) {
    return 'green'
  }
  
  return 'muted'
}

// Function to get the highest priority status for display
const getHighestPriorityStatus = (statuses: string[]) => {
  if (!statuses || statuses.length === 0) return null
  
  const lowerStatuses = statuses.map(s => s.toLowerCase())
  
  // Check orange statuses first
  for (const status of weedStatusColors.orange) {
    if (lowerStatuses.includes(status)) {
      return statuses.find(s => s.toLowerCase() === status)
    }
  }
  
  // Check yellow statuses
  for (const status of weedStatusColors.yellow) {
    if (lowerStatuses.includes(status)) {
      return statuses.find(s => s.toLowerCase() === status)
    }
  }
  
  // Check green statuses
  for (const status of weedStatusColors.green) {
    if (lowerStatuses.includes(status)) {
      return statuses.find(s => s.toLowerCase() === status)
    }
  }
  
  return statuses[0] // Return first status if no priority match
}

// Helper function to get total weed count for header
const getTotalWeedCount = () => {
  if (!weedResults.value) return 0
  return Object.values(weedResults.value).filter((result: any) => result.isWeed).length
}

// Helper function to get weed species list for header
const getWeedSpeciesList = () => {
  if (!weedResults.value) return []
  return Object.entries(weedResults.value)
    .filter(([_, result]: [string, any]) => result.isWeed)
    .map(([speciesName, _]) => speciesName)
}

// Helper function to get weed statuses for badges
const getWeedStatuses = (weedData: any[]) => {
  if (!weedData || !Array.isArray(weedData)) return []
  
  const allStatuses = weedData.flatMap((w: any) => w.statuses || [])
  const uniqueStatuses = [...new Set(allStatuses)]
  
  // Return up to 3 most important statuses
  return uniqueStatuses.slice(0, 3)
}

// Add navigation and layout refs
const activeSection = ref('weeds')
const tabsContainer = ref<HTMLElement | null>(null)
const weedsSection = ref<HTMLElement | null>(null)
const pestsSection = ref<HTMLElement | null>(null)
const { width: containerWidth } = useElementSize(tabsContainer)
const isNarrowWidth = computed(() => containerWidth.value < 300)

// Add sections data
const sections = computed(() => [
  { 
    id: 'weeds', 
    label: "Weeds",
    score: weedResults.value ? Object.values(weedResults.value).filter((result: any) => result.isWeed).length : null
  },
  { 
    id: 'pests', 
    label: "Pests",
    score: biosecurityResults.value ? Object.keys(biosecurityResults.value).length : null
  }
])

// Add scroll to section function
const scrollToSection = (sectionId: string) => {
  const section = document.getElementById(sectionId)
  const viewport = document.querySelector('[data-radix-scroll-area-viewport]')
  if (section && viewport) {
    const targetScroll = section.offsetTop - 120
    viewport.scrollTop = targetScroll
    activeSection.value = sectionId
  }
}

// Simple computed property for grouping species with identical pests
const displayResults = computed(() => {
  const results = biosecurityResults.value
  const grouped: Array<{ key: string, species: string[], data: ValidationData }> = []
  const processed = new Set<string>()

  Object.entries(results).forEach(([species, data]) => {
    if (processed.has(species)) return

    // Create pest signature for comparison
    const pestNames = (data.pests || [])
      .map(p => p.name?.replace(/\s*\([^)]*\)\s*/g, '').trim())
      .filter(Boolean)
      .sort()
      .join('|')

    // Find other species with identical pests
    const matchingSpecies = Object.entries(results)
      .filter(([otherSpecies, otherData]) => {
        if (otherSpecies === species || processed.has(otherSpecies)) return false
        
        const otherPestNames = (otherData.pests || [])
          .map(p => p.name?.replace(/\s*\([^)]*\)\s*/g, '').trim())
          .filter(Boolean)
          .sort()
          .join('|')
        
        return pestNames === otherPestNames && pestNames !== ''
      })
      .map(([s]) => s)

    const allSpecies = [species, ...matchingSpecies]
    allSpecies.forEach(s => processed.add(s))

    grouped.push({
      key: `group-${species}`,
      species: allSpecies,
      data
    })
  })

  return grouped
})

// Ref for species names
const speciesNames = ref<string[]>([])

// Add ref for tracking force refresh
const forceRefresh = ref(false)

// Cache status
const cacheStatus = computed(() => {
  if (!selectedProject.value?.id || speciesNames.value.length === 0) {
    return { hasCachedResults: false, cacheAge: null }
  }
  
  const cachedResults = validationResultsStore.getBiosecurityResults(
    speciesNames.value, 
      selectedProject.value.id.toString(),
      86400,
      typeof props.panel_id === 'string' ? props.panel_id : undefined
  )
  
  if (!cachedResults) {
    return { hasCachedResults: false, cacheAge: null }
  }
  
  const ageInSeconds = (Date.now() - cachedResults.timestamp) / 1000
  return { 
    hasCachedResults: true, 
    cacheAge: Math.round(ageInSeconds),
    cacheKey: validationResultsStore.hashSpeciesNames(speciesNames.value, selectedProject.value.id.toString())
  }
})

const clearCache = () => {
  if (selectedProject.value?.id) {
    console.log('[BiosecurityStatus] Clearing biosecurity cache')
    biosecurityResults.value = {}
    // Note: We could add a method to clear specific cache entries if needed
    // For now, user can call validationResultsStore.clearBiosecurityResults() manually
  }
}

// Listen for panel:data:validate events
const tool = ref(toolDefinitions.find(t => t.id === 'biosecurityValidation'))

// Debug logging to check the tool configuration
console.log('[BiosecurityStatus] Tool configuration:', tool.value)

// Add ref for tracking previous botanical names
const prevBotanicalNames = ref<string[]>([])

// Listen for validation events from the system
mitter.listen('panel:data:validate', (event: MitterEvents['panel:data:validate']) => {
  const botanicalNames = event?.data?.rowData?.flatMap((row: any) => 
    [row?.botanicalname || row?.botanical_name || row?.species]
  ).filter(Boolean).map((name: string) => name.trim())
  
  const hasNewNames = !isEqual(botanicalNames, prevBotanicalNames.value)
  
  if (event?.validation_tool_uid === tool?.value?.uid && hasNewNames) {
    speciesNames.value = botanicalNames
    prevBotanicalNames.value = botanicalNames
    refresh()
  }
})

// Function to refresh weeds data
const refreshWeeds = async (force: boolean = false) => {
  try {
    if (!speciesNames.value || speciesNames.value.length === 0) {
      console.log('[BiosecurityStatus] No species names to validate for weeds')
      return
    }
    
    console.log(`[BiosecurityStatus] Refreshing weeds with force=${force}`)
    
    const weedValidationResults = await validateWeeds(speciesNames.value, force)
    
    if (weedValidationResults) {
      console.log('[BiosecurityStatus] Weed validation results:', weedValidationResults)
      weedResults.value = weedValidationResults
    }
  } catch (error) {
    console.error('[BiosecurityStatus] Error refreshing weed data:', error)
  }
}

// Add ref for tracking expanded weed accordions
const expandedWeedAccordions = ref<Record<string, string>>({})

// Function to update weed accordion state
const updateWeedAccordionState = async (species: string, value: string) => {
  const wasExpanded = !!expandedWeedAccordions.value[species]
  expandedWeedAccordions.value[species] = value
  
  // Force reflow when opening accordion (not closing)
  if (value && !wasExpanded) {
    await nextTick()
    
    // Find and force reflow of the accordion content
    const accordionElement = document.querySelector(`[data-value="${species}"]`)
    if (accordionElement) {
      const contentElement = accordionElement.querySelector('[data-state="open"]') as HTMLElement
      if (contentElement) {
        // Force reflow by triggering layout recalculation
        contentElement.style.display = 'none'
        contentElement.offsetHeight // Trigger reflow
        contentElement.style.display = ''
      }
    }
  }
}

// Function to toggle weed accordion
const toggleWeedAccordion = (speciesName: string) => {
  const currentValue = expandedWeedAccordions.value[speciesName]
  const newValue = currentValue ? '' : speciesName
  updateWeedAccordionState(speciesName, newValue)
}

// Function to refresh with force flag
const refreshWithForceFlag = async (force: boolean = false) => {
  console.log(`[BiosecurityStatus] Refreshing with force=${force}`)
  forceRefresh.value = force
  await refresh()
}

// Function to refresh biosecurity data
const refresh = async () => {
  try {
    // Validate species names before proceeding
    if (!speciesNames.value || speciesNames.value.length === 0) {
      console.log('[BiosecurityStatus] No species names to validate')
      return
    }
    
    // Filter out invalid species names
    const validSpeciesNames = speciesNames.value.filter(name => 
      name && typeof name === 'string' && name.trim() !== ''
    )
    
    if (validSpeciesNames.length === 0) {
      console.log('[BiosecurityStatus] No valid species names to validate')
      return
    }
    
    if (!selectedProject.value?.id) {
      console.log('[BiosecurityStatus] No project ID available')
      return
    }
    
    const projectId = selectedProject.value.id.toString()
    
    // Check cache first if not forcing refresh
    if (!forceRefresh.value) {
      const cachedResults = validationResultsStore.getBiosecurityResults(
        validSpeciesNames, 
        projectId, 
        86400 // 24 hours default cache age
      )
      
      if (cachedResults) {
        console.log('[BiosecurityStatus] Using cached biosecurity results')
        biosecurityResults.value = cachedResults.results
        
        // Update biosecurity results store for fast colormap access
        updateBiosecurityResults(cachedResults.results)
        
        return
      }
    }
    
    console.log('[BiosecurityStatus] Fetching fresh biosecurity data for species:', validSpeciesNames)
    console.log('[BiosecurityStatus] Force refresh:', forceRefresh.value)
    console.log('[BiosecurityStatus] Note: Serper API calls are cached at server level with database fallback')
    
    // Process the validation results to get biosecurity data
    const validationResponse = await validateAndRefreshBiosecurity(
      validSpeciesNames, 
      undefined, // panel_id
      undefined, // country
      undefined, // libtype
      'en',      // codelang
      forceRefresh.value // forceRefresh flag
    )
    
    // Reset force refresh flag
    forceRefresh.value = false
    
    console.log('[BiosecurityStatus] Response structure:', {
      hasResult: !!validationResponse?.result,
      resultType: validationResponse?.result ? typeof validationResponse.result : 'undefined',
      resultKeys: validationResponse?.result ? Object.keys(validationResponse.result) : [],
      status: validationLoadingState.value
    })
    
    // Add more detailed debugging
    if (validationResponse?.result) {
      console.log('[BiosecurityStatus] Result content sample:', 
        Object.entries(validationResponse.result).slice(0, 1).map(([k, v]: [string, any]) => ({
          species: k,
          hasHosts: Array.isArray(v.hosts) && v.hosts.length > 0,
          hasPests: Array.isArray(v.pests) && v.pests.length > 0,
          pestCount: Array.isArray(v.pests) ? v.pests.length : 0
        }))
      )
    }
    
    // Early return if no results
    if (!validationResponse?.result) {
      console.warn('[BiosecurityStatus] No result property in validation response')
      return
    }
    
    if (validationResponse?.result) {
      // Log the raw result to understand its structure better
      console.log('[BiosecurityStatus] Raw result object:', validationResponse.result)
      
      const modifiedResult: Record<string, ValidationData> = {}
      
      // Create entries array and sort it (ensure we have entries to process)
      const entries = Object.entries(validationResponse.result || {})
      
      // If we have no entries, try to handle the cached response format
      if (entries.length === 0) {
        // Try to use our processor to ensure we're not filtering everything out
        const processedResponse = processBiosecurityResponse(validationResponse)
        if (Object.keys(processedResponse).length > 0) {
          console.log('[BiosecurityStatus] Using processed response with', Object.keys(processedResponse).length, 'entries')
          // Clear the old data first
          biosecurityResults.value = {}
          await nextTick()
          // Then set the new data
          biosecurityResults.value = processedResponse
          return
        }
        
        console.log('[BiosecurityStatus] No biosecurity results found')
        biosecurityResults.value = {}
        return
      }
      
      // Sort entries by presence of pests (most pests first)
      const sortedEntries = entries.sort(([speciesA, dataA], [speciesB, dataB]) => {
        const pestCountA = (dataA as ValidationData).pests?.length || 0
        const pestCountB = (dataB as ValidationData).pests?.length || 0
        return pestCountB - pestCountA
      })
      
      // Process the sorted entries
      sortedEntries.forEach(([species, data]) => {
        const typedData = data as ValidationData
        
        // Skip entries with no pests
        if (!typedData.pests || typedData.pests.length === 0) {
          console.log(`[BiosecurityStatus] Skipping ${species} - no pests found`)
          return
        }
        
        const pests = typedData.pests || []
        console.log(`[BiosecurityStatus] Processing ${species} with ${pests.length} pests`)
        
        // Ensure we have valid pest data
        const validPests = pests.filter(pest => pest?.name)
        
        // Skip entries with no valid pests
        if (validPests.length === 0) {
          console.log(`[BiosecurityStatus] Skipping ${species} - no valid pests found`)
          return
        }
        
        // Process pest data for display
        const majorHostPests = validPests.filter((pest: Pest) => pest.type === 'Major host')
        const otherPests = validPests.filter((pest: Pest) => pest.type !== 'Major host')
        const allPests = [...majorHostPests, ...otherPests]
        
        // Create unique pests list (as in the old version)
        const uniquePests = allPests.reduce((acc: Pest[], pest) => {
          const pestName = pest.name?.replace(/\s*\([^)]*\)\s*/g, '');
          if (pestName && !acc.some(p => p.name?.replace(/\s*\([^)]*\)\s*/g, '') === pestName)) {
            acc.push(pest);
          }
          return acc;
        }, []);
        
        // Include the species in the result even without images
        modifiedResult[species] = {
          ...typedData,
          hosts: typedData.hosts || [],
          pests: validPests,
          displayPests: uniquePests.length > 0 ? uniquePests.slice(0, 5).map(pest => ({
            ...pest,
            imageUrl: (() => {
              const imageUrl = typedData.images?.[allPests.indexOf(pest)]
              return imageUrl && !imageUrl.includes('error') ? imageUrl : null
            })()
          })) : [],
          totalPests: uniquePests.length || 0
        }
      })
      
      console.log('[BiosecurityStatus] Processed results:', modifiedResult)
      
      // Clear the old data first to force a complete re-render
      biosecurityResults.value = {}
      await nextTick()
      
      // Update the local ref with processed results
      biosecurityResults.value = modifiedResult
      
      // Update biosecurity results store for fast colormap access
      updateBiosecurityResults(modifiedResult)
      
      // Cache the results
      validationResultsStore.setBiosecurityResults(
        validSpeciesNames,
        projectId,
        modifiedResult
      )
      
      // Force another tick to ensure DOM updates
      await nextTick()
    } else {
      console.warn('[BiosecurityStatus] No result property in validation response')
    }
  } catch (error) {
    console.error('[BiosecurityStatus] Error refreshing biosecurity data:', error)
  }
}

// Add hovering states for each pest
const hoveringStates = ref<Record<string, boolean>>({})

// Function to update hovering state for a specific pest
const updateHoveringState = (pestName: string, value: boolean) => {
  hoveringStates.value[pestName] = value
  
  if (value && biosecurityResults.value) {
    const pest = Object.values(biosecurityResults.value)
      .flatMap(data => data.displayPests || [])
      .find(p => p?.name === pestName)
    
    if (pest?.imageUrl) {
      const img = new Image()
      img.src = pest.imageUrl
    }
  }
}

// Add ref for tracking expanded accordions
const expandedAccordions = ref<Record<string, string>>({})

// Function to update accordion state with reflow forcing
const updateAccordionState = async (species: string, value: string) => {
  const wasExpanded = !!expandedAccordions.value[species]
  expandedAccordions.value[species] = value
  
  // Force reflow when opening accordion (not closing)
  if (value && !wasExpanded) {
    await nextTick()
    
    // Find and force reflow of the accordion content
    const accordionElement = document.querySelector(`[data-value="${species}"]`)
    if (accordionElement) {
      const contentElement = accordionElement.querySelector('[data-state="open"]') as HTMLElement
      if (contentElement) {
        // Force reflow by triggering layout recalculation
        contentElement.style.display = 'none'
        contentElement.offsetHeight // Trigger reflow
        contentElement.style.display = ''
      }
    }
  }
}

// Function to toggle accordion
const toggleAccordion = (species: string) => {
  // Skip toggling for info cards
  const speciesData = biosecurityResults.value[species];
  if (speciesData?.isInfoCard) return;
  
  const currentValue = expandedAccordions.value[species]
  const newValue = currentValue ? '' : species
  updateAccordionState(species, newValue)
}

// Add this function to handle image load events
const onImageLoad = (event: Event) => {
  // The image has loaded - you could add loading states here if needed
  console.log('Image loaded:', event)
}

// Add a helper function to calculate the remaining pests count
const getRemainingPestsCount = (data: ValidationData) => {
  const totalPests = data.totalPests ?? data.pests?.length ?? 0
  const displayedPests = data.displayPests?.length || 0
  return Math.max(0, totalPests - displayedPests)
}

// Helper function to get species from project data - simplified version
const getProjectSpecies = async () => {
  try {
    const projectId = selectedProject.value?.id
    
    if (!projectId) {
      console.log('[BiosecurityStatus] No project ID available')
      return []
    }
    
    console.log('[BiosecurityStatus] Getting species for project:', projectId)
    
    // Add fallback for getting species directly from project data if available
    const projectData = selectedProject.value as any
    if (projectData?.plants && Array.isArray(projectData.plants)) {
      const projectPlants = projectData.plants
        .map((p: any) => p.botanical_name)
        .filter(Boolean)
      console.log('[BiosecurityStatus] Plants from project data:', projectPlants)
      return projectPlants
    }
    
    // If no project data, return empty array
    return []
  } catch (error) {
    console.error('[BiosecurityStatus] Error getting project species:', error)
    return []
  }
}

// Check to see if we have a valid response but it's being filtered out
const processBiosecurityResponse = (response: any): Record<string, ValidationData> => {
  if (!response || !response.result) {
    console.warn('[BiosecurityStatus] No result in response to process')
    return {}
  }

  // Log the raw response to help with debugging
  console.log('[BiosecurityStatus] Processing raw response:', 
    Object.entries(response.result).length > 0 ? 
    Object.entries(response.result).map(([k, v]: [string, any]) => ({ 
      species: k, 
      hasPests: Array.isArray((v as any).pests) && (v as any).pests.length > 0 
    })) :
    'No entries')
  
  // If we have results but they're being filtered out in the component, bypass the filtering
  if (Object.entries(response.result).length > 0) {
    // Return the result with minimal processing to ensure display
    return response.result
  }
  
  return {}
}

// Watch for changes in species names to check cache
watch(() => speciesNames.value, (newSpeciesNames, oldSpeciesNames) => {
  if (!selectedProject.value?.id) return
  
  // Only proceed if we have species names and they've actually changed
  if (newSpeciesNames.length > 0 && JSON.stringify(newSpeciesNames) !== JSON.stringify(oldSpeciesNames)) {
    console.log('[BiosecurityStatus] Species names changed, checking cache...')
    
    // Check if we have cached results for this species list
    const cachedResults = validationResultsStore.getBiosecurityResults(
      newSpeciesNames, 
      selectedProject.value.id.toString()
    )
    
    if (cachedResults) {
      console.log('[BiosecurityStatus] Found cached results for updated species list')
      biosecurityResults.value = cachedResults.results
      
      // Update biosecurity results store for fast colormap access
      updateBiosecurityResults(cachedResults.results)
    } else {
      console.log('[BiosecurityStatus] No cached results found, will need fresh data')
      // Clear existing results since we don't have cache for new species list
      biosecurityResults.value = {}
    }
  }
}, { immediate: false })

// Add automatic weeds validation when species names are available
watch(() => speciesNames.value, async (newSpeciesNames, oldSpeciesNames) => {
  // Only proceed if we have new species names and they've actually changed
  if (newSpeciesNames.length > 0 && JSON.stringify(newSpeciesNames) !== JSON.stringify(oldSpeciesNames)) {
    console.log('[BiosecurityStatus] Species names changed, automatically checking for weeds...')
    
    // Automatically trigger weeds validation
    try {
      await refreshWeeds(false) // Don't force refresh, use cache if available
    } catch (error) {
      console.error('[BiosecurityStatus] Error in automatic weeds validation:', error)
    }
  }
}, { immediate: false })

// Add debug watcher to log state changes - these can stay
watch(speciesNames, (newValue) => {
  console.log('[BiosecurityStatus] Species names changed:', newValue)
})

watch(biosecurityResults, (newValue) => {
  console.log('[BiosecurityStatus] Biosecurity results changed:', 
    Object.keys(newValue).length > 0 ? 'Has results' : 'No results',
    Object.keys(newValue))
}, { deep: true })

// Add a function to handle image load errors
const handleImageError = (pest: Pest) => {
  console.warn(`Image failed to load for pest: ${pest.name}`)
  // Set the imageUrl to null to show the fallback icon
  if (pest.imageUrl) {
    pest.imageUrl = null
  }
}

// Add intersection observer setup
onMounted(() => {
  // Wait for scroll container to be available
  nextTick(() => {
    // Find the scroll container (same as DiversityStatus.vue)
    const scrollContainer = document.querySelector('.scroll-container')
    
    const sectionRefs = {
      weeds: weedsSection,
      pests: pestsSection
    }
    
    Object.entries(sectionRefs).forEach(([id, ref]) => {
      const element = ref.value
      if (element) {
        useIntersectionObserver(
          element,
          ([{ isIntersecting }]) => {
            if (isIntersecting) {
              activeSection.value = id
            }
          },
          {
            threshold: [0.1, 0.5, 0.9], // Multiple thresholds for better detection
            rootMargin: '-100px 0px 0px 0px', // Offset to account for sticky header
            root: scrollContainer // Use the scroll container as root
          }
        )
      }
    })
  })
})


</script>

<style scoped>
.hover-expanded {
  transform: scale(1.1);
  opacity: 0;
  z-index: 50;
  pointer-events: auto;
  cursor: none;
  transition: all 80ms ease-out;
}

/* Clean up conflicting styles */
:deep(.lens-container) {
  border-radius: 50%;
  position: relative !important;
  inset: auto !important;
  width: 100% !important;
  height: 100% !important;
  overflow: visible !important;
}

:deep(.motion-container) {
  opacity: 1 !important;
  z-index: 100;
  pointer-events: none;
  border-radius: 50% !important;
  overflow: hidden !important;
  /* Adjust motion container positioning to accommodate larger lens */
  transform: translate3d(-50%, -50%, 0) scale(1.5) !important;
}

/* Make the magnified content circular */
:deep(.lens-content) {
  border-radius: 50% !important;
  overflow: hidden !important;
  mask-image: radial-gradient(circle, white 100%, transparent 100%);
  -webkit-mask-image: radial-gradient(circle, white 100%, transparent 100%);
  /* Ensure content scales properly with larger lens */
  transform-origin: center !important;
}

/* Ensure the image inside lens-content is also masked */
:deep(.lens-content img) {
  border-radius: 50%;
  mask-image: radial-gradient(circle, white 100%, transparent 100%);
  -webkit-mask-image: radial-gradient(circle, white 100%, transparent 100%);
}

/* Remove previous masking that might interfere */
:deep(.motion-container) {
  mask-image: none;
  -webkit-mask-image: none;
}

/* Remove all the conflicting styles */
.pest-lens,
.scale-\[4\],
.scale-\[4\].opacity-0,
.invisible-but-active,
.scale-\[2\]:hover {
  display: none;
}

/* Keep other necessary styles */
.sticky-header {
  min-height: 52px;
  display: flex;
  align-items: center;
}

.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.cursor-pointer {
  transition: opacity 0.2s ease;
}
</style>